/**
 * Peshni shared form validation — one module reused by every lead/enquiry
 * form on the site (Contact page, Bulk/B2B enquiry page form, header
 * "Bulk Inquiry" modal, footer newsletter signup, WhatsApp inquiry modal).
 *
 * Any <form class="peshni-form--validate"> is auto-wired on DOMContentLoaded
 * (and re-wired on shopify:section:load, since the theme editor can swap a
 * section's markup in via AJAX without a full page reload): required fields
 * are checked, email/Indian-mobile formats are validated, inline error
 * messages are shown/cleared next to each field, the submit button is
 * disabled with a "Sending…" state to block duplicate clicks, and — only
 * once everything passes — the native submit is allowed through unchanged
 * (Shopify's contact/customer forms have no JSON API, so the reliable
 * approach is the same full-page POST + reload Shopify already does; this
 * module only gates it with proper validation). Mobile-number fields are
 * additionally sanitized on every keystroke/paste so they can never hold
 * more than 10 digits in the first place.
 *
 * window.PeshniForms is also exposed so a form with its own bespoke
 * submit handler (e.g. the WhatsApp inquiry modal, which never touches
 * Shopify's backend) can call validateForm()/setLoading() directly
 * instead of re-implementing the same checks.
 */
(function () {
  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  }

  function isValidIndianMobile(value) {
    var digits = value.replace(/\D/g, '');
    return /^[6-9]\d{9}$/.test(digits);
  }

  function isLikelyName(value) {
    var trimmed = value.trim();
    return trimmed.length > 0 && !/^\d+$/.test(trimmed);
  }

  function fieldType(field) {
    return (field.dataset.peshniType || field.type || '').toLowerCase();
  }

  function isMobileField(field) {
    var type = fieldType(field);
    return type === 'mobile' || type === 'tel';
  }

  function sanitizeMobileValue(field) {
    var original = field.value || '';
    var digitsOnly = original.replace(/\D/g, '').slice(0, 10);
    if (digitsOnly === original) return;

    var caret = field.selectionStart;
    var removedBeforeCaret = caret == null ? 0 : (caret - digitsOnly.length > 0 ? caret - digitsOnly.length : 0);
    field.value = digitsOnly;
    if (caret != null) {
      var newCaret = Math.max(0, Math.min(digitsOnly.length, caret - removedBeforeCaret));
      try {
        field.setSelectionRange(newCaret, newCaret);
      } catch (e) {
        /* some input types don't support selection ranges */
      }
    }
  }

  function getWrapper(field) {
    return (
      field.closest('.field') ||
      field.closest('.peshni-contact-form__field') ||
      field.parentElement
    );
  }

  function clearFieldError(field) {
    var wrapper = getWrapper(field);
    if (!wrapper) return;
    wrapper.classList.remove('peshni-field--invalid');
    var existing = wrapper.querySelector('.peshni-field-error');
    if (existing) existing.remove();
  }

  function setFieldError(field, message) {
    clearFieldError(field);
    var wrapper = getWrapper(field);
    if (!wrapper) return;
    wrapper.classList.add('peshni-field--invalid');
    var error = document.createElement('span');
    error.className = 'peshni-field-error';
    error.setAttribute('role', 'alert');
    error.textContent = message;
    wrapper.appendChild(error);
  }

  function validateField(field) {
    if (field.type === 'checkbox') {
      if (field.hasAttribute('required') && !field.checked) {
        setFieldError(field, 'Please check this box to continue.');
        return false;
      }
      clearFieldError(field);
      return true;
    }

    if (isMobileField(field)) {
      sanitizeMobileValue(field);
    }

    var trimmed = (field.value || '').trim();
    var type = fieldType(field);
    var isRequired = field.hasAttribute('required');

    if (isRequired && trimmed === '') {
      var requiredMessage = 'This field is required.';
      if (type === 'email') requiredMessage = 'Please enter your email address.';
      else if (type === 'mobile' || type === 'tel') requiredMessage = 'Please enter your mobile number.';
      else if (type === 'name') requiredMessage = 'Please enter your name.';
      setFieldError(field, requiredMessage);
      return false;
    }

    if (trimmed === '') {
      clearFieldError(field);
      return true;
    }

    if (type === 'email') {
      if (!isValidEmail(trimmed)) {
        setFieldError(field, 'Please enter a valid email address.');
        return false;
      }
    } else if (type === 'mobile' || type === 'tel') {
      if (!isValidIndianMobile(trimmed)) {
        setFieldError(field, 'Please enter a valid 10-digit mobile number.');
        return false;
      }
    } else if (type === 'name') {
      if (!isLikelyName(trimmed)) {
        setFieldError(field, 'Please enter your name.');
        return false;
      }
    }

    clearFieldError(field);
    return true;
  }

  function validateForm(form) {
    var fields = form.querySelectorAll('input, textarea, select');
    var firstInvalid = null;
    var allValid = true;

    fields.forEach(function (field) {
      if (field.type === 'hidden' || field.type === 'submit' || field.type === 'button') return;
      var ok = validateField(field);
      if (!ok) {
        allValid = false;
        if (!firstInvalid) firstInvalid = field;
      }
    });

    if (firstInvalid) firstInvalid.focus();
    return allValid;
  }

  function setLoading(form, loading, loadingText) {
    var button = form.querySelector('button[type="submit"], input[type="submit"]');
    if (!button) return;
    if (loading) {
      if (!button.dataset.peshniOriginalText) {
        button.dataset.peshniOriginalText = button.textContent.trim();
      }
      button.textContent = loadingText || 'Sending…';
      button.disabled = true;
      button.classList.add('is-loading');
    } else {
      if (button.dataset.peshniOriginalText) {
        button.textContent = button.dataset.peshniOriginalText;
      }
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }

  function showSuccessModal(modalId) {
    var modal = document.getElementById(modalId || 'PeshniFormSuccessModal');
    if (modal && typeof modal.show === 'function') modal.show();
  }

  function initForm(form) {
    if (form.dataset.peshniWired === '1') return;
    form.dataset.peshniWired = '1';

    form.querySelectorAll('input, textarea').forEach(function (field) {
      if (isMobileField(field)) sanitizeMobileValue(field);
    });

    form.addEventListener(
      'input',
      function (event) {
        var field = event.target;
        if (!field.matches('input, textarea, select')) return;

        if (isMobileField(field)) {
          sanitizeMobileValue(field);
        }

        var wrapper = getWrapper(field);
        if (wrapper && wrapper.classList.contains('peshni-field--invalid')) {
          validateField(field);
        }
      },
      true
    );

    // Defense in depth: 'input' already catches paste in every modern
    // browser, but re-sanitizing a tick after paste guards against any
    // browser/extension that mutates the value outside the normal input
    // pipeline (e.g. some autofill or clipboard-manager extensions).
    form.addEventListener(
      'paste',
      function (event) {
        var field = event.target;
        if (!isMobileField(field)) return;
        window.setTimeout(function () {
          sanitizeMobileValue(field);
        }, 0);
      },
      true
    );

    form.addEventListener('submit', function (event) {
      if (form.dataset.peshniSubmitting === '1') {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }

      if (!validateForm(form)) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }

      form.dataset.peshniSubmitting = '1';
      setLoading(form, true);
      // No preventDefault here — the native submit proceeds exactly as
      // before (Shopify's own POST + redirect). Each form's own section
      // checks form.posted_successfully? server-side after the reload to
      // trigger the shared success modal.
    });
  }

  function wireAll() {
    document.querySelectorAll('form.peshni-form--validate').forEach(initForm);
  }

  document.addEventListener('DOMContentLoaded', wireAll);
  // The Shopify theme editor swaps section markup in via AJAX on save,
  // which replaces the <form> element without firing DOMContentLoaded
  // again — re-scan so forms re-rendered that way still get wired.
  document.addEventListener('shopify:section:load', wireAll);

  window.PeshniForms = window.PeshniForms || {};
  Object.assign(window.PeshniForms, {
    validateForm: validateForm,
    validateField: validateField,
    isValidEmail: isValidEmail,
    isValidIndianMobile: isValidIndianMobile,
    isLikelyName: isLikelyName,
    showSuccessModal: showSuccessModal,
    setLoading: setLoading,
    sanitizeMobileValue: sanitizeMobileValue,
  });
})();
